import {
  CheckboxController,
  Collapse,
  Text,
} from "@pushpress/shared-components";
import { Button, RichTextEditor, Textarea } from "@pushpress/pushpress-ui";
import { type Control, useWatch, useFormContext, Controller } from "react-hook-form";
import { PlanForm } from "../PlanDrawer.types";
import { useContext, useState, useRef } from "react";
import { useGeneratePlanText } from "../../../../apis/aiApi.hooks";
import { TextType } from "../../../../apis/aiApi";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../../utils/misc";
import { Icons } from "../../../../icons";
import { cn } from "../../../../utils/css";
import { AppContext } from "../../../../context/app";
import { RichInputInstance } from "@pushpress/shared-components/build/components/inputs/RichInput/RichInput.types";
import styles from "./SalesSection.module.scss";

interface SalesSectionProps {
  control: Control<PlanForm>;
}

const LoadingOverlay = () => (
  <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/30">
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
      <div className="tw-relative tw-w-8 tw-h-8">
        <div className="tw-absolute tw-top-0 tw-left-0 tw-w-8 tw-h-8 tw-border-2 tw-border-gray-200 tw-rounded-full"></div>
        <div
          className="tw-absolute tw-top-0 tw-left-0 tw-w-8 tw-h-8 tw-border-2 tw-border-blue-500 tw-rounded-full tw-animate-spin"
          style={{
            borderTopColor: "transparent",
            animationDuration: "1s",
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
          }}
        />
      </div>
      <span className="tw-text-sm tw-text-black">Generating text...</span>
    </div>
  </div>
);

const SalesSection = (props: SalesSectionProps) => {
  const { client } = useContext(AppContext);
  const { control } = props;
  const { setValue, getValues } = useFormContext<PlanForm>();
  const generatePlanText = useGeneratePlanText();
  const [generatingStates, setGeneratingStates] = useState<
    Record<TextType, boolean>
  >({
    confirmationMessage: false,
    shortDescription: false,
    longDescription: false,
  });
  const canGenerateMessages = client?.bankConnected === 1;

  const [publishToPublic, classAccess] = useWatch({
    control,
    name: ["publishToPublic", "classAccess"],
  });

  const richInputRefs = useRef<Record<TextType, RichInputInstance | null>>({
    confirmationMessage: null,
    shortDescription: null,
    longDescription: null,
  });
  
  // Safety check to ensure control is available
  if (!control) {
    return <div>Loading...</div>;
  }

  const handleGenerate = async (textType: TextType) => {
    const planData = getValues();

    if (!planData) {
      toast.error(
        "Cannot generate text: Missing plan data",
        defaultToastOptions,
      );
      return;
    }

    try {
      setGeneratingStates((prev) => ({ ...prev, [textType]: true }));
      const { completion } = await generatePlanText.mutateAsync({
        textType,
        plan: planData,
      });

      if (
        textType === "confirmationMessage" ||
        textType === "longDescription"
      ) {
        richInputRefs.current[textType]?.setHtmlValue(completion);
      }

      setValue(textType, completion, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      setGeneratingStates((prev) => ({ ...prev, [textType]: false }));
    } catch (error) {
      toast.error(
        "Failed to generate text. Please try again.",
        defaultToastOptions,
      );
      setGeneratingStates((prev) => ({ ...prev, [textType]: false }));
    }
  };

  const GenerateButton = ({
    textType,
    label,
  }: {
    textType: TextType;
    label: string;
  }) => (
    <Button
      variant="outline"
      size="sm"
      onClick={() => handleGenerate(textType)}
      disabled={generatingStates[textType]}
      className="tw-mt-2"
    >
      {generatingStates[textType] ? (
        <span>Generating...</span>
      ) : (
        <div className="tw-flex tw-items-center tw-gap-1">
          <Icons.Sparkles className="tw-w-3 tw-h-3 tw-text-yellow-400" />{" "}
          {label}
        </div>
      )}
    </Button>
  );

  return (
    <div>
      <Text
        component="h1"
        variant="heading-4"
        weight="600"
        color="wolf-800"
        className="mt-4"
      >
        Online Purchase Page
      </Text>

      <CheckboxController
        control={control}
        name="publishToPublic"
        label="Enable online purchase page"
        className="tw-mt-3"
      />

      <Collapse in={publishToPublic}>
        <div className="tw-relative tw-mt-3">
          <div className="tw-relative">
            <label htmlFor="shortDescription">
              Short description
            </label>
            <Controller
              control={control}
              name="shortDescription"
              render={({ field }) => (
                <Textarea
                  {...field}
                  placeholder="Short description"
                  className="tw-mt-1 tw-h-[200px]"
                  disabled={generatingStates.shortDescription}
                />
              )}
            />
            {canGenerateMessages && (
              <div className="tw-absolute tw-top-1/2 tw-right-4 tw--translate-y-1/2 tw-z-10">
                <GenerateButton textType="shortDescription" label="Generate" />
              </div>
            )}
            {generatingStates.shortDescription && <LoadingOverlay />}
          </div>
        </div>
        <div className="tw-relative tw-mt-3">
          <div className="tw-relative">
            <label htmlFor="longDescription">
              Long description
            </label>
            <Controller
              control={control}
              name="longDescription"
              render={({ field }) => (
                <RichTextEditor
                  key={`longDescription-${field.value || 'empty'}`}
                  content={field.value || ""}
                  onChange={(value) => {
                    field.onChange(value);
                  }} 
                  height="tw-h-[200px]"
                  className={cn(
                    "tw-mt-1",
                    styles["sales-description-container"],
                    generatingStates.longDescription && "tw-opacity-10",
                    )}
                />
              )}
            />
            {canGenerateMessages && (
              <div className="tw-absolute tw-top-1/2 tw-right-4 tw--translate-y-1/2 tw-z-10">
                <GenerateButton textType="longDescription" label="Generate" />
              </div>
            )}
            {generatingStates.longDescription && <LoadingOverlay />}
          </div>
        </div>

        <div className="tw-relative tw-mt-3">
          <div className="tw-relative tw-grid tw-gap-1.5">
            <label htmlFor="confirmationMessage">
              Confirmation message
            </label>
            <Controller
              control={control}
              name="confirmationMessage"
              render={({ field }) => (
                <RichTextEditor
                  key={`confirmationMessage-${field.value || 'empty'}`}
                  content={field.value || ""}
                  onChange={(value) => {
                    field.onChange(value);
                  }}
                  height="tw-h-[200px]"
                />
              )}
            />
            {canGenerateMessages && (
              <div className="tw-absolute tw-top-1/2 tw-right-4 tw--translate-y-1/2 tw-z-10">
                <GenerateButton
                  textType="confirmationMessage"
                  label="Generate"
                />
              </div>
            )}
            {generatingStates.confirmationMessage && <LoadingOverlay />}
          </div>
        </div>

        {classAccess.value !== "none" && (
          <>
            <CheckboxController
              control={control}
              name="allowClassBooking"
              label="Allow class booking after purchase"
            />

            {/* <Text variant="body-md" color="wolf-600" className="tw-mt-1">
              After purchase, should clients be prompted to reserve a class?
            </Text> */}
          </>
        )}

        <Text component="p" variant="body-lg" className="!tw-mt-6">
          Link to this plan&apos;s purchase page from:
        </Text>

        <div className="tw-flex tw-flex-row tw-gap-4">
          <CheckboxController
            control={control}
            name="sellOnMemberApp"
            label="Members app"
            className="tw-mt-1"
          />
          <CheckboxController
            control={control}
            name="sellOnStaffApp"
            label="Kiosk mode (staff app)"
            className="tw-mt-1"
          />
          <CheckboxController
            control={control}
            name="sellOnWebsitePlanList"
            label="Website plan list"
            className="tw-mt-1"
          />
        </div>
      </Collapse>
    </div>
  );
};

export default SalesSection;
